/**
 * Simplified XR hook that works with existing splatv renderer
 */

import { useState, useCallback, useRef } from 'react'
import { XRServiceSimple } from '@/services/xr/xr-service-simple'
import type { CameraState } from '@/types/camera'

export interface XRState {
  isSupported: boolean
  arSupported: boolean
  vrSupported: boolean
  isActive: boolean
  sessionType: 'vr' | 'ar' | null
  error: string | null
}

export interface XROptions {
  onSessionStart?: () => void
  onSessionEnd?: () => void
}

export function useXRSimple(options: XROptions = {}) {
  const [state, setState] = useState<XRState>({
    isSupported: false,
    arSupported: false,
    vrSupported: false,
    isActive: false,
    sessionType: null,
    error: null
  })
  
  const xrServiceRef = useRef<XRServiceSimple | null>(null)
  const rendererRef = useRef<any>(null)
  
  // Initialize XR service
  if (!xrServiceRef.current) {
    xrServiceRef.current = new XRServiceSimple({
      onSessionStart: () => {
        setState(prev => ({ ...prev, isActive: true }))
        options.onSessionStart?.()
      },
      onSessionEnd: () => {
        setState(prev => ({ ...prev, isActive: false, sessionType: null }))
        options.onSessionEnd?.()
      }
    })
  }
  
  // Check XR support
  const checkSupport = useCallback(async () => {
    console.log('useXRSimple: checkSupport called')
    if (!xrServiceRef.current) {
      console.error('No XR service available')
      return
    }
    
    try {
      const support = await xrServiceRef.current.checkSupport()
      console.log('XR support result:', support)
      setState(prev => ({
        ...prev,
        isSupported: support.ar || support.vr,
        arSupported: support.ar,
        vrSupported: support.vr
      }))
    } catch (error) {
      console.error('Failed to check XR support:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to check XR support'
      }))
    }
  }, [])
  
  // Set renderer reference
  const setRenderer = useCallback((renderer: any) => {
    rendererRef.current = renderer
  }, [])
  
  // Enter VR
  const enterVR = useCallback(async (defaultCamera?: CameraState) => {
    if (!xrServiceRef.current || !rendererRef.current) {
      console.error('XR service or renderer not initialized')
      return
    }
    
    try {
      setState(prev => ({ ...prev, sessionType: 'vr' }))
      await xrServiceRef.current.startSession(rendererRef.current, 'vr', defaultCamera)
    } catch (error) {
      console.error('Failed to enter VR:', error)
      setState(prev => ({
        ...prev,
        isActive: false,
        sessionType: null,
        error: error instanceof Error ? error.message : 'Failed to enter VR'
      }))
    }
  }, [])
  
  // Enter AR
  const enterAR = useCallback(async (defaultCamera?: CameraState) => {
    if (!xrServiceRef.current || !rendererRef.current) {
      console.error('XR service or renderer not initialized')
      return
    }
    
    try {
      setState(prev => ({ ...prev, sessionType: 'ar' }))
      await xrServiceRef.current.startSession(rendererRef.current, 'ar', defaultCamera)
    } catch (error) {
      console.error('Failed to enter AR:', error)
      setState(prev => ({
        ...prev,
        isActive: false,
        sessionType: null,
        error: error instanceof Error ? error.message : 'Failed to enter AR'
      }))
    }
  }, [])
  
  // Exit XR
  const exitXR = useCallback(async () => {
    if (!xrServiceRef.current) return
    
    try {
      await xrServiceRef.current.endSession()
    } catch (error) {
      console.error('Failed to exit XR:', error)
    }
  }, [])
  
  return {
    state,
    checkSupport,
    setRenderer,
    enterVR,
    enterAR,
    exitXR
  }
}